
import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import './App.css';
import './mobile.css';
import { initMobileUtils } from './utils/mobileUtils';
import { ThemeProvider } from './context/ThemeContext';

// Layout Components
import Navbar from './layout/Navbar';
import Footer from './layout/Footer';
import ScrollToTop from './components/ScrollToTop';
import ContactSticky from './components/ContactSticky';

// Lazy-loaded components for better performance
const Home = lazy(() => import('./components/Home'));
const About = lazy(() => import('./components/About'));
const Contect = lazy(() => import('./components/Contect'));
const GetQuote = lazy(() => import('./components/GetQuote'));
const Careers = lazy(() => import('./components/careers'));
const Insights = lazy(() => import('./components/Insights'));
const PillarDetail = lazy(() => import('./components/PillarDetail'));
const CaseStudies = lazy(() => import('./components/CaseStudies'));
const Capabilities = lazy(() => import('./components/Capabilities'));
const LearnMoreAboutUs = lazy(() => import('./components/LearnMoreAboutUs'));
const CoreValues = lazy(() => import('./components/CoreValues'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const TermsOfUse = lazy(() => import('./components/TermsOfUse'));

// Service Components
const ServicesMenu = lazy(() => import('./components/ServicesMenu'));
const WebDevelopment = lazy(() => import('./components/services/WebDevelopment'));
const ITConsulting = lazy(() => import('./components/services/ITConsulting.js'));
const ITConsultingArchitectureReview = lazy(() => import('./components/services/ITConsultingArchitectureReview.js'));
const CustomSoftwareArchitectureReview = lazy(() => import('./components/services/CustomSoftwareArchitectureReview.js'));
const DataEngineeringArchitectureReview = lazy(() => import('./components/services/DataEngineeringArchitectureReview.js'));
const CloudNativeDevOpsArchitectureReview = lazy(() => import('./components/services/CloudNativeDevOpsArchitectureReview.js'));
const WebDevelopmentArchitectureReview = lazy(() => import('./components/services/WebDevelopmentArchitectureReview.js'));
const DigitalProductEngineeringArchitectureReview = lazy(() => import('./components/services/DigitalProductEngineeringArchitectureReview.js'));
const Customsof = lazy(() => import('./components/services/Customsof'));
const CloudNativeDevOps = lazy(() => import('./components/services/CloudNativeDevOps'));
const DigitalProductEngineering = lazy(() => import('./components/services/DigitalProductEngineering'));
const DataEngineeringIntelligence = lazy(() => import('./components/services/DataEngineeringIntelligence'));
const AIProductEngineering = lazy(() => import('./components/services/ai-product-engineering'));
const ModernDataPlatforms = lazy(() => import('./components/services/modern-data-platforms'));

// Loading Component
const LoadingSpinner = () => (
  <div className="d-flex justify-content-center align-items-center min-vh-100" style={{ background: 'var(--cg-bg, #f5f7fb)' }}>
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);

// Error Fallback Component
const ErrorFallback = ({ error, resetErrorBoundary }) => (
  <div className="container py-5">
    <div className="row justify-content-center">
      <div className="col-md-8 text-center">
        <h2 className="text-danger mb-4">Oops! Something went wrong</h2>
        <p className="text-muted mb-4">
          We're sorry, but something unexpected happened. Please try refreshing the page.
        </p>
        <button 
          className="btn btn-primary" 
          onClick={resetErrorBoundary}
        >
          Try Again
        </button>
        <details className="mt-4">
          <summary>Error Details</summary>
          <pre className="text-start mt-2">{error.message}</pre>
        </details>
      </div>
    </div>
  </div>
);


function App() {
  // Initialize mobile utilities
  useEffect(() => {
    const cleanup = initMobileUtils();
    return cleanup;
  }, []);

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => window.location.reload()}
    >
      <ThemeProvider>
      <Router>
        <ScrollToTop />
        <Navbar />
        <main className="cg-shell">
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/about/learn-more" element={<LearnMoreAboutUs />} />
              <Route path="/about/core-values" element={<CoreValues />} />
              <Route path="/about/pillar/:pillarId" element={<PillarDetail />} />
              <Route path="/contact" element={<Contect />} />
              <Route path="/quote" element={<GetQuote />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-use" element={<TermsOfUse />} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/insight" element={<Insights />} />
              <Route path="/case-studies" element={<CaseStudies />} />
              <Route path="/capabilities" element={<Capabilities />} />
              <Route path="/services" element={<ServicesMenu />} />
              <Route path="/services/web-development" element={<WebDevelopment />} />
              <Route path="/services/cloudservices" element={<CloudNativeDevOps />} />
              <Route path="/services/cloud-native-devops" element={<CloudNativeDevOps />} />
              <Route path="/services/itconsulting" element={<ITConsulting />} />
              <Route path="/services/itconsulting/architecture-review" element={<ITConsultingArchitectureReview />} />
              <Route path="/services/Customsof/architecture-review" element={<CustomSoftwareArchitectureReview />} />
              <Route path="/services/data-engineering-intelligence/architecture-review" element={<DataEngineeringArchitectureReview />} />
              <Route path="/services/cloud-native-devops/architecture-review" element={<CloudNativeDevOpsArchitectureReview />} />
              <Route path="/services/web-development/architecture-review" element={<WebDevelopmentArchitectureReview />} />
              <Route path="/services/saas/architecture-review" element={<DigitalProductEngineeringArchitectureReview />} />
              <Route path="/services/Customsof" element={<Customsof />} />
              <Route path="/services/saas" element={<DigitalProductEngineering />} />
              <Route path="/services/data-engineering-intelligence" element={<DataEngineeringIntelligence />} />
              <Route path="/services/ai-product-engineering" element={<AIProductEngineering />} />
              <Route path="/services/modern-data-platforms" element={<ModernDataPlatforms />} />
            </Routes>
          </Suspense>
        </main>
        <Footer />
        <ContactSticky />
      </Router>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
